import type { JSX } from 'react';
import { useEffect, useState } from 'react';

const THIRTY_MINUTES_MS = 30 * 60 * 1000;
const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

export interface CountdownTimerProps {
  /** ISO 8601 with offset, from the server — this component never computes its own cutoff. */
  cutoffAt: string;
}

/**
 * `portal-orders/spec.md` → "Countdown se calcula sobre el timestamp del
 * servidor" (RF-06-06): relative to `cutoffAt`, never to the browser's
 * local clock — `Date` subtraction is UTC-epoch based, so this is
 * correct regardless of the viewer's timezone. Submitting after the
 * cutoff is still rejected server-side (`ORDER_CUTOFF_PASSED`)
 * independently of what this timer shows.
 */
export function CountdownTimer({ cutoffAt }: CountdownTimerProps): JSX.Element {
  const [remainingMs, setRemainingMs] = useState(() => remaining(cutoffAt));

  useEffect(() => {
    const interval = setInterval(() => setRemainingMs(remaining(cutoffAt)), 1000);

    return () => clearInterval(interval);
  }, [cutoffAt]);

  if (remainingMs <= 0) {
    return (
      <span data-testid="countdown-timer" className="text-sm font-semibold text-slate-500">
        Corte alcanzado
      </span>
    );
  }

  const tone =
    remainingMs < THIRTY_MINUTES_MS
      ? 'text-red-600'
      : remainingMs < TWO_HOURS_MS
        ? 'text-amber-600'
        : 'text-slate-700';

  return (
    <span data-testid="countdown-timer" className={`text-sm font-semibold ${tone}`}>
      {formatRemaining(remainingMs)} para el corte
    </span>
  );
}

function remaining(cutoffAt: string): number {
  return new Date(cutoffAt).getTime() - Date.now();
}

function formatRemaining(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
}
